import type { Theme } from "../types/Theme";
import { controlBackgroundColor, progressBarValueColor } from "./Mixins";

/**
 * Modern progress edge width. Legacy caps at 1px (`Math.min(theme.BorderWidth, 1)`) because a thick
 * edge on a ~28px bar reads badly; modern caps at its own 2px design value for the same reason, but
 * honours Field Settings → Borders = none.
 */
export const MODERN_PROGRESS_BORDER_WIDTH_PX = 2;

export function resolveProgressBorderWidth(theme: Theme): number {
    return theme.BorderWidth === 0 ? 0 : MODERN_PROGRESS_BORDER_WIDTH_PX;
}

/** Solid track border for the modern segmented progress bar. */
export function resolveProgressTrackBorder(theme: Theme): string {
    return `${resolveProgressBorderWidth(theme)}px solid ${theme.ControlBorderColor}`;
}

/** Fill segment border uses the track background to create an inset separator. */
export function resolveProgressFillBorder(theme: Theme): string {
    return `${resolveProgressBorderWidth(theme)}px solid ${controlBackgroundColor(theme)}`;
}

/**
 * Track corner radius — Field Settings → Corners ({@link Theme.BorderRadius}) as pill math against the
 * bar height. Only ever reached for a non-floating modern bar: both this and
 * {@link resolveProgressFillBorderRadius} are consumed solely by the `progressModern*` classes, which
 * render behind `useModernBasic` (`UseModernTheme && ProgressBarType !== "floating"`). A floating bar is
 * squared by the legacy `bar` rule instead.
 */
export function resolveProgressTrackBorderRadius(theme: Theme): number {
    return Math.min(theme.BorderRadius, theme.ProgressBarHeight / 2);
}

/**
 * Fill segment radius for the modern segmented bar. The fill sits inside the track, inset by the
 * border width, so its corners align with the track's inner edge.
 */
export function resolveProgressFillBorderRadius(theme: Theme): number {
    return Math.max(0, resolveProgressTrackBorderRadius(theme) - resolveProgressBorderWidth(theme));
}

/** Label and percentage text color in the progress label row. */
export function resolveProgressLabelColor(theme: Theme): string {
    return progressBarValueColor(theme, 100);
}
